import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '../store/project-store';
import { useTasks, useSprints } from '../hooks/useProject';
import { useLocale } from '../hooks/useLocale';
import { formatLocaleDate } from '../utils/locale';
function generateStatusReport(tasks, locale) {
    const counts = {
        backlog: 0,
        'to-do': 0,
        'in-progress': 0,
        'in-review': 0,
        done: 0,
    };
    tasks.forEach((t) => { counts[t.status]++; });
    const total = tasks.length;
    return `# Status Report

Generated: ${formatLocaleDate(new Date(), locale)}

## Summary

| Status | Count | Percentage |
|--------|-------|------------|
| Backlog | ${counts.backlog} | ${total > 0 ? Math.round((counts.backlog / total) * 100) : 0}% |
| To Do | ${counts['to-do']} | ${total > 0 ? Math.round((counts['to-do'] / total) * 100) : 0}% |
| In Progress | ${counts['in-progress']} | ${total > 0 ? Math.round((counts['in-progress'] / total) * 100) : 0}% |
| In Review | ${counts['in-review']} | ${total > 0 ? Math.round((counts['in-review'] / total) * 100) : 0}% |
| Done | ${counts.done} | ${total > 0 ? Math.round((counts.done / total) * 100) : 0}% |

**Total Tasks:** ${total}`;
}
function generateSprintReport(tasks, sprints, locale) {
    const activeSprint = sprints.find((s) => s.status === 'in-progress');
    if (!activeSprint)
        return '# Sprint Report\n\nNo active sprint found.';
    const sprintTasks = tasks.filter((t) => t.sprint === activeSprint.id);
    const done = sprintTasks.filter((t) => t.status === 'done').length;
    const total = sprintTasks.length;
    const points = sprintTasks.reduce((s, t) => s + (t.storyPoints || 0), 0);
    const donePoints = sprintTasks
        .filter((t) => t.status === 'done')
        .reduce((s, t) => s + (t.storyPoints || 0), 0);
    return `# Sprint Report: ${activeSprint.name}

Generated: ${formatLocaleDate(new Date(), locale)}

## Progress

- **Tasks:** ${done}/${total} completed (${total > 0 ? Math.round((done / total) * 100) : 0}%)
- **Story Points:** ${donePoints}/${points} completed (${points > 0 ? Math.round((donePoints / points) * 100) : 0}%)
- **Goal:** ${activeSprint.goal || 'No goal set'}

## Task Breakdown

| Status | Count |
|--------|-------|
| To Do | ${sprintTasks.filter((t) => t.status === 'to-do').length} |
| In Progress | ${sprintTasks.filter((t) => t.status === 'in-progress').length} |
| In Review | ${sprintTasks.filter((t) => t.status === 'in-review').length} |
| Done | ${sprintTasks.filter((t) => t.status === 'done').length} |`;
}
function generateVelocityReport(sprints, locale) {
    const completed = sprints.filter((s) => s.status === 'completed');
    const velocities = completed.map((s) => s.velocity);
    const avg = velocities.length > 0
        ? Math.round(velocities.reduce((a, b) => a + b, 0) / velocities.length)
        : 0;
    return `# Velocity Report

Generated: ${formatLocaleDate(new Date(), locale)}

## Sprint Velocity

| Sprint | Committed | Completed | Velocity |
|--------|-----------|-----------|----------|
${completed.map((s) => `| ${s.name} | ${s.committedPoints} pts | ${s.completedPoints} pts | ${s.velocity} |`).join('\n') || '| No completed sprints | — | — | — |'}

**Average Velocity:** ${avg} pts/sprint`;
}
function generateFlowReport(tasks, locale) {
    const total = tasks.length;
    const done = tasks.filter((t) => t.status === 'done').length;
    const inProgress = tasks.filter((t) => t.status === 'in-progress').length;
    return `# Flow Metrics Report

Generated: ${formatLocaleDate(new Date(), locale)}

## Key Metrics

| Metric | Value |
|--------|-------|
| Total Tasks | ${total} |
| Completed | ${done} |
| In Progress | ${inProgress} |
| Completion Rate | ${total > 0 ? Math.round((done / total) * 100) : 0}% |

## Work In Progress

${inProgress} task${inProgress !== 1 ? 's' : ''} currently in progress.`;
}
function renderMarkdown(md) {
    const lines = md.split('\n');
    let html = '';
    let inTable = false;
    for (const line of lines) {
        const trimmed = line.trim();
        // Table separator row (|---|---|)
        if (/^\|[\s-:|]+\|$/.test(trimmed)) {
            continue;
        }
        // Table row
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
            const cells = trimmed
                .slice(1, -1)
                .split('|')
                .map((c) => c.trim());
            const isHeader = cells.every((c) => /^[-]+$/.test(c));
            if (isHeader)
                continue;
            if (!inTable) {
                html += '<table class="w-full border-collapse"><tbody>';
                inTable = true;
            }
            const row = cells
                .map((c) => `<td class="px-3 py-2 text-sm text-text-secondary border-b border-card-border">${c}</td>`)
                .join('');
            html += `<tr>${row}</tr>`;
        }
        else {
            if (inTable) {
                html += '</tbody></table>';
                inTable = false;
            }
            // Headings
            if (trimmed.startsWith('# ')) {
                html += `<h1 class="text-2xl font-bold text-text-primary mb-4">${trimmed.slice(2)}</h1>`;
            }
            else if (trimmed.startsWith('## ')) {
                html += `<h2 class="text-lg font-semibold text-text-primary mt-6 mb-3">${trimmed.slice(3)}</h2>`;
            }
            else if (trimmed.startsWith('- ')) {
                html += `<li class="text-sm text-text-secondary ml-4">${trimmed.slice(2)}</li>`;
            }
            else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
                html += `<p class="text-sm text-text-secondary font-semibold">${trimmed.slice(2, -2)}</p>`;
            }
            else if (trimmed === '') {
                html += '<br/>';
            }
            else {
                const formatted = trimmed.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>');
                html += `<p class="text-sm text-text-secondary">${formatted}</p>`;
            }
        }
    }
    if (inTable)
        html += '</tbody></table>';
    return html;
}
export function ReportsView() {
    const { projectId } = useParams();
    const { tasks } = useTasks(projectId);
    const { sprints } = useSprints(projectId);
    const { t } = useTranslation();
    const { locale } = useLocale();
    const projects = useProjectStore((s) => s.projects);
    const project = projects.find((p) => p.project_id === projectId);
    const [reportType, setReportType] = useState('status');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const reportLabels = {
        status: t('reports.status') || 'Status Report',
        sprint: t('sprint.title') + ' Report',
        velocity: t('reports.velocity'),
        flow: t('reports.throughput'),
    };
    const filteredTasks = useMemo(() => {
        if (!startDate && !endDate)
            return tasks;
        return tasks.filter((t) => {
            const d = new Date(t.created);
            if (startDate && d < new Date(startDate))
                return false;
            if (endDate && d > new Date(endDate))
                return false;
            return true;
        });
    }, [tasks, startDate, endDate]);
    const reportContent = useMemo(() => {
        switch (reportType) {
            case 'status':
                return generateStatusReport(filteredTasks, locale);
            case 'sprint':
                return generateSprintReport(filteredTasks, sprints, locale);
            case 'velocity':
                return generateVelocityReport(sprints, locale);
            case 'flow':
                return generateFlowReport(filteredTasks, locale);
        }
    }, [reportType, filteredTasks, sprints, locale]);
    const handleExport = (format) => {
        if (format === 'md') {
            const blob = new Blob([reportContent], { type: 'text/markdown' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${reportType}-report.md`;
            a.click();
            URL.revokeObjectURL(url);
        }
        else if (format === 'html') {
            const html = `<!DOCTYPE html><html><head><title>${reportLabels[reportType]}</title><style>body{font-family:sans-serif;max-width:800px;margin:0 auto;padding:20px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f5f5f5}</style></head><body>${renderMarkdown(reportContent)}</body></html>`;
            const blob = new Blob([html], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${reportType}-report.html`;
            a.click();
            URL.revokeObjectURL(url);
        }
        else if (format === 'csv') {
            const rows = reportContent
                .split('\n')
                .filter((l) => l.startsWith('|'))
                .map((l) => l
                .split('|')
                .filter((c) => c.trim())
                .map((c) => `"${c.trim().replace(/"/g, '""')}"`)
                .join(','));
            const csv = rows.join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${reportType}-report.csv`;
            a.click();
            URL.revokeObjectURL(url);
        }
    };
    return (_jsxs("div", { className: "space-y-6", children: [_jsx("div", { className: "flex items-center justify-between", children: _jsxs("h2", { className: "text-2xl font-bold text-text-primary", children: [t('nav.reports'), " \u2014 ", project?.name || projectId] }) }), _jsxs("div", { className: "flex flex-wrap gap-3 items-center", children: [_jsx("div", { className: "flex gap-1 bg-bg-secondary rounded-lg p-1", children: Object.keys(reportLabels).map((type) => (_jsx("button", { onClick: () => setReportType(type), className: `px-3 py-1.5 text-sm rounded-md transition-colors ${reportType === type
                                ? 'bg-primary text-white'
                                : 'text-text-secondary hover:bg-bg-primary'}`, children: reportLabels[type] }, type))) }), _jsxs("div", { className: "flex gap-2 items-center ml-auto", children: [_jsx("input", { type: "date", value: startDate, onChange: (e) => setStartDate(e.target.value), className: "input" }), _jsx("span", { className: "text-text-muted", children: "to" }), _jsx("input", { type: "date", value: endDate, onChange: (e) => setEndDate(e.target.value), className: "input" })] })] }), _jsxs("div", { className: "flex gap-2", children: [_jsxs("button", { onClick: () => handleExport('md'), className: "btn-secondary text-sm", children: [t('actions.export'), " MD"] }), _jsxs("button", { onClick: () => handleExport('html'), className: "btn-secondary text-sm", children: [t('actions.export'), " HTML"] }), _jsxs("button", { onClick: () => handleExport('csv'), className: "btn-secondary text-sm", children: [t('actions.export'), " CSV"] })] }), _jsx("div", { className: "card p-6", children: _jsx("div", { className: "prose prose-sm max-w-none text-text-secondary", dangerouslySetInnerHTML: { __html: renderMarkdown(reportContent) } }) })] }));
}
//# sourceMappingURL=ReportsView.js.map